import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, FormControlLabel, Radio, RadioGroup, Stack, Typography } from '@mui/material';
import { EmployeeInput } from 'domain/shared/employee';
import { EnumDropdown, useFormContext, ZoneDateTimeInput, ZonedApiDateFormat } from 'framework/form';
import type { FormFieldsData } from 'framework/form';
import { CreateEditFormDrawer } from 'framework/ViewTypes/contentLayout';
import type { CreateEditFormSection } from 'framework/ViewTypes/contentLayout';
import { SCHEDULE_MODES } from '../constants';
import type { ScheduleMode } from '../constants';
import applicationScheduleInterviewValidation from '../validation/applicationScheduleInterviewValidation';

type ApplicationScheduleInterviewDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
};

const INTERVIEW_TYPES = {
  SCREENING: 'Screening interview',
  ROLE_RELATED: 'Role related interview',
  GENERAL: 'General interview',
  CLIENT: 'Client interview',
  EXECUTIVE: 'Executive interview',
};

const INTERVIEW_METHODS = {
  ONLINE: 'Online',
  OFFLINE: 'Offline',
};

const getScheduleMode = (data: FormFieldsData): ScheduleMode =>
  data.scheduleMode in SCHEDULE_MODES ? (data.scheduleMode as ScheduleMode) : SCHEDULE_MODES.SELF_BOOKING;

const SUBMIT_TITLES: Record<ScheduleMode, string> = {
  SELF_BOOKING: 'Generate link',
  SELF_BOOKING_SELECTED: 'Generate link',
  MANUAL: 'Schedule',
};

type ScheduleModeCardProps = {
  mode: ScheduleMode;
  title: string;
  description: string;
  isSelected: boolean;
  hasAffordance?: boolean;
  children?: React.ReactNode;
};

const ScheduleModeCard = ({ mode, title, description, isSelected, hasAffordance, children }: ScheduleModeCardProps) => (
  <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
    <Stack direction="row" alignItems="flex-start" gap={1}>
      <FormControlLabel
        value={mode}
        control={<Radio sx={{ p: 0, mr: 1.2, width: 20, height: 20 }} />}
        sx={{ m: 0, flex: 1, alignItems: 'flex-start' }}
        label={
          <Typography variant="body1" fontWeight={600}>
            {title}
          </Typography>
        }
      />
      {hasAffordance && <ChevronRightIcon fontSize="small" color="action" />}
    </Stack>
    <Typography variant="body1" pt={1.25}>
      {description}
    </Typography>

    {isSelected && children ? (
      <Stack direction="row" gap={2} sx={{ pt: 1.25 }}>
        {children}
      </Stack>
    ) : null}
  </Box>
);

const ScheduleModeField = () => {
  const { data, onChangeCallback } = useFormContext();
  const selectedMode = getScheduleMode(data ?? {});

  return (
    <RadioGroup
      value={selectedMode}
      onChange={(_event, value) => onChangeCallback?.({ scheduleMode: value })}
      sx={{ gap: 2 }}
    >
      <ScheduleModeCard
        mode={SCHEDULE_MODES.SELF_BOOKING}
        title="Generate self-booking link"
        description="Candidate will choose a time slot from all interviewers available for this interview type"
        isSelected={selectedMode === SCHEDULE_MODES.SELF_BOOKING}
      />

      <ScheduleModeCard
        mode={SCHEDULE_MODES.SELF_BOOKING_SELECTED}
        title="Generate self-booking link for selected interviewers"
        description="Candidate will choose a time slot only from the selected interviewers’ availability"
        isSelected={selectedMode === SCHEDULE_MODES.SELF_BOOKING_SELECTED}
      >
        <Box sx={{ width: 260 }}>
          <EmployeeInput source="interviewer" label="Interviewers" isRequired />
        </Box>
      </ScheduleModeCard>

      <ScheduleModeCard
        mode={SCHEDULE_MODES.MANUAL}
        title="Schedule manually"
        description="Select the interviewer and interview time yourself. Use this for pre-agreed or non-standard slots"
        isSelected={selectedMode === SCHEDULE_MODES.MANUAL}
        hasAffordance
      >
        <Box>
          <EmployeeInput source="interviewer" label="Interviewer" isRequired />
        </Box>
        <Box>
          <ZoneDateTimeInput
            source="interviewDateTime"
            label="Interview date and time"
            apiDateFormat={ZonedApiDateFormat.ISO_8601_DATETIME_WITH_OFFSET}
            isRequired
          />
        </Box>
      </ScheduleModeCard>
    </RadioGroup>
  );
};

const SCHEDULE_INTERVIEW_SECTIONS: CreateEditFormSection[] = [
  {
    id: 'interview',
    fields: [
      {
        id: 'interviewType',
        content: <EnumDropdown source="interviewType" label="Interview type" options={INTERVIEW_TYPES} isRequired />,
      },
      {
        id: 'interviewMethod',
        content: (
          <EnumDropdown source="interviewMethod" label="Interview method" options={INTERVIEW_METHODS} isRequired />
        ),
      },
      {
        id: 'scheduleMode',
        size: { xs: 12, md: 12, xl: 12 },
        content: <ScheduleModeField />,
      },
    ],
  },
];

const ApplicationScheduleInterviewDrawer = ({ isOpen, onClose }: ApplicationScheduleInterviewDrawerProps) => (
  <CreateEditFormDrawer
    isOpen={isOpen}
    onClose={onClose}
    title="Schedule interview"
    initialData={{
      interviewType: 'SCREENING',
      interviewMethod: 'ONLINE',
      scheduleMode: SCHEDULE_MODES.SELF_BOOKING,
    }}
    isCreateRequest
    getValidationSchemaFunc={() => Promise.resolve(applicationScheduleInterviewValidation)}
    submitFormFunc={async () => {}}
    afterSubmit={{ execute: onClose, message: 'Interview scheduled.' }}
    submitButtonTitle={data => SUBMIT_TITLES[getScheduleMode(data)]}
    handleDirty={false}
    getSections={() => SCHEDULE_INTERVIEW_SECTIONS}
  />
);

export default ApplicationScheduleInterviewDrawer;
